import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast, Flip } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '@utils/axiosInstance';

import { setSelectedLanguage } from '@modules/subtitle';
import SubEditor from './SubEditor';
import NoSubContent from './NoSubContent';
import LanguageSelectorPanel from './LanguageSelectorPanel';
import langListData from '@resources/subLangList.json';
import '../../../../../../resources/assets/styles/subtitle.scss';
import SkeletonSubtitleTab from './SkeletonSubtitleTab';
import Loading from '../../../../_commons/LoadingComponent';

function SubtitleTab() {
    const dispatch = useDispatch();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { userToken, organizationData } = useSelector(state => state.organization);

    const [availableSubtitleLanguages, setAvailableSubtitleLanguages] = useState<{ lang: string; state: number }[]>([]); // 현재 영상에서 선택할 수 있는 모든 언어를 담은 배열
    const [currentSelectedLanguage, setCurrentSelectedLanguage] = useState({}); // 현재 탭에서 선택된 언어
    const [showNoSub, setShowNoSub] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false); // 자막 추가 모달 열기
    const [isLoading, setIsLoading] = useState(true); // 로딩 상태 추가
    const [noData, setNoData] = useState(false);

    const getLangFromPath = () => {
        const pathSegments = location.pathname.split('/');
        const subtitleIndex = pathSegments.findIndex(segment => segment === 'subtitle');
        return (subtitleIndex !== -1 && pathSegments.length > subtitleIndex + 1)
            ? pathSegments[subtitleIndex + 1]
            : null;
    };

    const navigateWithoutLang = () => {
        const pathSegments = location.pathname.split('/');
        const subtitleIndex = pathSegments.findIndex(segment => segment === 'subtitle');

        if (subtitleIndex !== -1) {
            const newPath = pathSegments.slice(0, subtitleIndex + 1).join('/')
            navigate(newPath, { replace: true });
        }
    };

    const navigateWithLang = (lang) => {
        const pathSegments = location.pathname.split('/');
        const subtitleIndex = pathSegments.findIndex(segment => segment === 'subtitle');

        if (subtitleIndex !== -1) {
            const newPath = pathSegments.slice(0, subtitleIndex + 1).join('/') + '/' + lang;
            navigate(newPath, { replace: true });
        }
    };

    // 현재 영상에서 생성된 자막 목록 가져오기
    useEffect(() => {
        setIsLoading(true);

        api({
            method: 'post',
            url: '/subtitle/getState',
            data: {
                tk: userToken,
                uuid,
                ogc: organizationData.organization_code,
                type: "ALL"
            }
        })
            .then(response => {
                setIsLoading(false);
                if (response.data.code === 100) {
                    const subtitleLangs = response.data.data.subtitle;

                    if (subtitleLangs.length > 0) {
                        sortSubtitles(response.data.data.subtitle);
                    } else {
                        setAvailableSubtitleLanguages([]);
                        setCurrentSelectedLanguage({});
                        setShowNoSub(true);
                        navigateWithoutLang();
                        return;
                    }

                    const langFromPath = getLangFromPath();
                    console.log('langFromPath', langFromPath);
                    if (langFromPath) { // URL에 언어가 있는 경우
                        const matchingLanguage = subtitleLangs.find(slang => slang.lang === langFromPath);
                        if (matchingLanguage) { // URL의 언어가 존재하는 경우
                            setCurrentSelectedLanguage(matchingLanguage);
                            dispatch(setSelectedLanguage(matchingLanguage));
                            navigateWithLang(matchingLanguage.lang);
                            setShowNoSub(false);
                        } else { // URL의 언어가 존재하지 않는 경우
                            toast.error(t('vod.manage.invalid_language_access'), {
                                position: "top-center",
                                transition: Flip
                            });
                            setCurrentSelectedLanguage(subtitleLangs[0]);
                            dispatch(setSelectedLanguage(subtitleLangs[0]));
                            navigateWithLang(subtitleLangs[0].lang);
                            setShowNoSub(false);
                        }
                    } else { // URL에 언어가 없는 경우
                        if (subtitleLangs.length > 0) { // 생성된 언어가 있는 경우
                            setCurrentSelectedLanguage(subtitleLangs[0]);
                            dispatch(setSelectedLanguage(subtitleLangs[0]));
                            setShowNoSub(false);
                            navigateWithLang(subtitleLangs[0].lang);
                        } else { // 생성된 언어가 없는 경우
                            setCurrentSelectedLanguage({});
                            dispatch(setSelectedLanguage({}));
                            setShowNoSub(true);
                        }
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching subtitle state:', error);
                setAvailableSubtitleLanguages([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [userToken, uuid, organizationData]);

    // 자막 목록 정렬
    const sortSubtitles = (subtitles: { lang: string; state: number }[]) => {
        const processedSubtitles = subtitles.map(subtitle => {
            const normalLang = langListData.languages.find(
                lang => lang.lang === subtitle.lang
            );
            if (normalLang) {
                return { ...normalLang, state: subtitle.state };
            }
            return null; // 정상적인 언어가 아닌 경우 null 반환
        }).filter((sub): sub is { lang: string; state: number } => sub !== null); // null 값 필터링

        setAvailableSubtitleLanguages(processedSubtitles);
    };

    useEffect(() => { // url에서 직접 언어 변경했을 때 처리
        if (availableSubtitleLanguages.length > 0) {
            const langFromPath = getLangFromPath();
            console.log('langFromPath', langFromPath, availableSubtitleLanguages);
            if (langFromPath) { // URL에 언어가 있는 경우
                const matchingLanguage = availableSubtitleLanguages.find(clang => clang.lang === langFromPath);
                console.log('matchingLanguage', matchingLanguage, noData);

                if (matchingLanguage && matchingLanguage.state === 2 && noData) { // state가 2이고 데이터가 없는 경우
                    setCurrentSelectedLanguage(availableSubtitleLanguages[0]);
                    dispatch(setSelectedLanguage(availableSubtitleLanguages[0]));
                    navigateWithLang(availableSubtitleLanguages[0].lang);
                    setShowNoSub(false);
                } else if (matchingLanguage) { // URL의 언어가 존재하는 경우
                    setCurrentSelectedLanguage(matchingLanguage);
                    dispatch(setSelectedLanguage(matchingLanguage));
                    setShowNoSub(false);
                } else { // URL의 언어가 존재하지 않는 경우
                    toast.error(t('vod.manage.invalid_language_access'), {
                        position: "top-center",
                        transition: Flip
                    });
                    setCurrentSelectedLanguage(availableSubtitleLanguages[0]);
                    dispatch(setSelectedLanguage(availableSubtitleLanguages[0]));
                    navigateWithLang(availableSubtitleLanguages[0].lang);
                    setShowNoSub(false);
                }
            } else { // URL에 언어가 없는 경우
                if (availableSubtitleLanguages.length > 0) { // 생성된 언어가 있는 경우
                    setCurrentSelectedLanguage(availableSubtitleLanguages[0]);
                    dispatch(setSelectedLanguage(availableSubtitleLanguages[0]));
                    setShowNoSub(false);
                    navigateWithLang(availableSubtitleLanguages[0].lang);
                } else { // 생성된 언어가 없는 경우
                    setCurrentSelectedLanguage({});
                    dispatch(setSelectedLanguage({}));
                    setShowNoSub(true);
                }
            }
        } else {
            // 자막이 하나도 없는 경우 - URL에서 언어가 있으면 제거
            const langFromPath = getLangFromPath();
            if (langFromPath) {
                console.log('No subtitles available, removing language from URL');
                navigateWithoutLang();
            }
            setCurrentSelectedLanguage({});
            dispatch(setSelectedLanguage({}));
            setShowNoSub(true);
        }
    }, [location.pathname, availableSubtitleLanguages, noData]);

    return (
        <div className='tab-video-subtitle'>
            {isLoading ?
                <Loading />
                : !showNoSub ? (
                    <div className="wrapper-subtitle-content">
                        <LanguageSelectorPanel
                            isAddModalOpen={isAddModalOpen}
                            setIsAddModalOpen={setIsAddModalOpen}
                            currentSelectedLanguage={currentSelectedLanguage}
                            setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                            availableSubtitleLanguages={availableSubtitleLanguages}
                            setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                            isLoading={isLoading}
                            setShowNoSub={setShowNoSub}
                        />

                        {availableSubtitleLanguages.length > 0 && (
                            <SubEditor
                                setNoData={setNoData}
                                availableSubtitleLanguages={availableSubtitleLanguages}
                            />
                        )}
                    </div>
                ) : (
                    <div style={{ width: '30%', margin: '0 auto', paddingTop: '10%' }}>
                        <NoSubContent
                            availableSubtitleLanguages={availableSubtitleLanguages}
                            setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                            setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                            setIsAddModalOpen={setIsAddModalOpen}
                            setShowNoSub={setShowNoSub}
                        />
                    </div>
                )}
        </div>
    );
}

export default SubtitleTab;
